import React from "react";
import { Phone, MapPin, Clock } from "lucide-react";
import { useSection } from "@/context/SiteContext";
import { telHref } from "@/lib/site";

export default function Contact() {
  const c = useSection("contact");
  const h = useSection("hours");
  const tel = telHref(c.phone_tel, c.phone);
  const mapQuery = encodeURIComponent(c.maps_query || c.address || "");
  const hoursRows = [
    { days: h.weekdays_days || "Vardagar", time: h.weekdays_time || "11:00 – 21:00" },
    { days: h.friday_days || "Fredag", time: h.friday_time || "11:00 – 22:00" },
    { days: h.saturday_days || "Lördag", time: h.saturday_time || "12:00 – 22:00" },
    { days: h.sunday_days || "Söndag", time: h.sunday_time || "12:00 – 21:00" },
  ];

  return (
    <section id="kontakt" className="py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 bg-[var(--text)] text-white rounded-3xl overflow-hidden">
          <div className="p-8 sm:p-12 lg:p-16 space-y-8">
            <span className="inline-block text-xs uppercase tracking-[0.22em] font-semibold text-[var(--primary)]">
              {c.eyebrow || "Kontakt"}
            </span>
            <h2 className="font-serif-display text-4xl sm:text-5xl font-black tracking-tight leading-[1.05]">
              {c.title_line1} <br />
              <span className="italic font-medium text-[var(--primary)]">{c.title_italic}</span>
            </h2>

            <ul className="space-y-6">
              <li className="flex items-start gap-4">
                <span className="w-11 h-11 rounded-full bg-white/10 grid place-items-center shrink-0">
                  <MapPin size={18} />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/60 font-semibold">Adress</div>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${mapQuery}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-address-link"
                    className="text-lg font-semibold hover:text-[var(--primary)] transition-colors"
                  >
                    {c.address}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-11 h-11 rounded-full bg-white/10 grid place-items-center shrink-0">
                  <Phone size={18} />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/60 font-semibold">Telefon</div>
                  <a href={tel} data-testid="contact-phone-link" className="text-lg font-semibold hover:text-[var(--primary)] transition-colors">
                    {c.phone}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-11 h-11 rounded-full bg-white/10 grid place-items-center shrink-0">
                  <Clock size={18} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-xs uppercase tracking-widest text-white/60 font-semibold">Öppettider</div>
                  <ul className="mt-1 space-y-0.5">
                    {hoursRows.map((row) => (
                      <li key={row.days} className="flex justify-between gap-4 text-sm">
                        <span className="text-white/85">{row.days}</span>
                        <span className="font-semibold tabular-nums">{row.time}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            </ul>

            <a href={tel} data-testid="contact-call-button" className="inline-flex items-center gap-2 rounded-full bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white px-7 py-4 font-semibold transition-all hover:-translate-y-0.5">
              <Phone size={18} /> {c.cta_text || "Ring & boka bord"}
            </a>
          </div>

          <div className="relative min-h-[360px] lg:min-h-full">
            <iframe
              title="Karta"
              src={`https://www.google.com/maps?q=${mapQuery}&output=embed`}
              className="absolute inset-0 w-full h-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
